import React from 'react';
import { FormCloseConfirmation } from './FormCloseConfirmation';

interface WithFormProtectionProps {
  initialData: any;
  currentData: any;
  onClose?: (hasUnsavedChanges?: boolean) => void;
}

type WrappedComponentProps<P> = Omit<P, keyof WithFormProtectionProps> & WithFormProtectionProps;

export const withFormProtection = <P extends object>(
  WrappedComponent: React.ComponentType<P & WithFormProtectionProps>
) => {
  return React.forwardRef<{ handleClose: () => void; isDirty: () => boolean; isValid?: () => boolean }, WrappedComponentProps<P>>((props, ref) => {
    const { initialData, currentData, onClose, ...rest } = props as WithFormProtectionProps & { isValid?: () => boolean };
    const [isConfirmationVisible, setIsConfirmationVisible] = React.useState(false);
    const onCloseRef = React.useRef(onClose);
    const isValid = (props as any).isValid as (() => boolean) | undefined;

    React.useEffect(() => {
      onCloseRef.current = onClose;
    }, [onClose]);

    const isDirty = React.useCallback(() => {
      if (!initialData || !currentData) return false;

      // Преобразуем объекты в строки для сравнения, игнорируя undefined и null значения
      const normalizeData = (data: any) => {
        const normalized = { ...data };
        Object.keys(normalized).forEach(key => {
          if (normalized[key] === undefined || normalized[key] === null) {
            normalized[key] = '';
          }
          if (typeof normalized[key] === 'string') {
            normalized[key] = normalized[key].trim();
          }
        });
        return normalized;
      };

      const normalizedInitial = normalizeData(initialData);
      const normalizedCurrent = normalizeData(currentData);
      return JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedCurrent);
    }, [initialData, currentData]);

    const handleConfirmClose = React.useCallback(() => {
      setIsConfirmationVisible(false);
      if (onCloseRef.current) {
        onCloseRef.current(false);
      }
    }, []);

    const handleCancelClose = React.useCallback(() => {
      setIsConfirmationVisible(false);
    }, []);

    const handleClose = React.useCallback(() => {
      // Если форма невалидна, не даём закрыть окно
      if (typeof isValid === 'function' && !isValid()) {
        return;
      }
      const hasUnsavedChanges = isDirty();
      if (hasUnsavedChanges) {
        setIsConfirmationVisible(true);
        if (onCloseRef.current) {
          onCloseRef.current(true);
        }
      } else if (onCloseRef.current) {
        onCloseRef.current(false);
      }
    }, [isDirty, isValid]);

    // Forward the ref
    React.useImperativeHandle(ref, () => ({
      handleClose,
      isDirty,
      isValid
    }), [handleClose, isDirty, isValid]);

    return (
      <>
        <WrappedComponent
          {...(rest as P)}
          initialData={initialData}
          currentData={currentData}
          onClose={handleClose}
        />
        <FormCloseConfirmation
          isVisible={isConfirmationVisible}
          onConfirm={handleConfirmClose}
          onCancel={handleCancelClose}
        />
      </>
    );
  });
};
